import React, { useEffect, useRef, useState } from "react";
import { useTable } from "react-table";
import styles from "../../../style/cssModules/table/TableComponent.module.css"; // Import your CSS Module
import { FaSearch } from "react-icons/fa"; // Import the search icon
import searchIcon from "../../images/icons/searchIcon.svg";
import addIcon from "../../images/icons/addIcon.svg";
import ButtonLoading from "../../loaders/ButtonLoading";
import Stats from "./Stats";
import { ChevronDown } from "lucide-react"; // Import the ChevronDown icon
import PageLoader from "../../loaders/PageLoading";
import { useTranslation } from "react-i18next";
import useRTLContext from "../../../hooks/useRTLContext";
import "./stats.css";

const TableComponent = ({
  statsData = [],
  noExport = false,
  exportLoading = false,
  handleExportClick = () => {},
  headerDescription = "",
  handleCloseMenu,
  dropdownRef,
  threeDotsRef,
  columns,
  data,
  tableSections = null,
  activeTableSection = null,
  handleActiveTableSectionChange,
  handleOnModal,
  tableHeader = true,
  headerTitle = "Title",
  headerPrimaryBtnName = null,
  headerPrimaryBtnClick = () => {},
  headerSecondaryBtnClick = null,
  headerSecondaryBtnName = "",
  tabsOptions = null,
  activeTab = "Religion",
  handleTabChange,
  isTable = true,
  isRead = true,
  draftBtn = null,
  onClickDraft,
  draftCount = null,
  draftView = false,
  allEmpCount = null,
  totalCount = "",
  additionalStats = null,
  handleAdditionalStatsClick,
  noData = false,
  searchTerm = "",
  handleSearchTermDebounce = () => {},
  loading = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [exportQty, setExportQty] = useState(10); // Default export quantity
  const [exportFormat, setExportFormat] = useState("csv"); // Default export format
  const [toggleSearch, setToggleSearch] = useState(false);

  useEffect(() => {
    if (searchTerm && !toggleSearch) {
      setToggleSearch(true);
    }
  }, [searchTerm, toggleSearch]);
  useEffect(() => {
    // Fallback function if handleCloseMenu is not provided
    const closeMenu = handleCloseMenu || (() => {});

    const handleClickOutside = (event) => {
      // Check if refs are defined and the click is outside
      if (
        dropdownRef?.current &&
        !dropdownRef.current.contains(event.target) &&
        threeDotsRef?.current &&
        !threeDotsRef.current.contains(event.target)
      ) {
        closeMenu(); // Close the dropdown
      }
    };

    // Add event listener only if refs are defined
    if (dropdownRef && threeDotsRef) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, threeDotsRef, handleCloseMenu]);

  // Filter data based on search term
  // const filteredData = data.filter((row) => {
  //   return Object.values(row).some((value) =>
  //     String(value).toLowerCase().includes(searchTerm.toLowerCase())
  //   );
  // });

  // Prepare the table
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  return (
    <>
      <div className={styles.tableSection}>
        {tableHeader && (
          <div className={styles.headerContainer}>
            <div className={styles.headerContentContainer}>
              <div className="flex flex-col">
                <div className={styles.headerTitleContainer}>
                  <div className={styles.headerTitle}>{headerTitle}</div>
                  {totalCount && (
                    <span
                      className={styles.titleCount}
                    >{`(${totalCount})`}</span>
                  )}
                </div>
                {headerDescription && (
                  <p className="text-sm text-[#475467] font-normal">
                    {t(headerDescription)}
                  </p>
                )}
              </div>
              <div className={styles.headerActionContainer}>
                {isRead && (
                  <>
                    <div className={styles.headerSearchContainer}>
                      {(toggleSearch || searchTerm) && (
                        <input
                          type="text"
                          placeholder={t("Search by name...")}
                          value={searchTerm}
                          onChange={(e) =>
                            handleSearchTermDebounce(e.target.value)
                          }
                          className={styles.searchInput}
                        />
                      )}
                      <button
                        className={styles.headerSearchBtnContainer}
                        onClick={() => {
                          if (toggleSearch) {
                            handleSearchTermDebounce("");
                          }

                          setToggleSearch(!toggleSearch);
                        }}
                      >
                        <img src={searchIcon} alt="search icon" />
                      </button>
                    </div>

                    {!noExport && (
                      <button
                        className={styles.headerExportContainer}
                        disabled={exportLoading}
                        onClick={handleExportClick}
                        style={{ marginLeft: "8px" }}
                      >
                        <label className={styles.headerExportLabel}>
                          {exportLoading ? (
                            <ButtonLoading thisColor={"#ddd"} />
                          ) : (
                            t("Export")
                          )}
                        </label>
                      </button>

                      // <button
                      // onClick={handleExportClick}
                      //   disabled={exportLoading}
                      //   className="inline-flex items-center gap-2 rounded-lg bg-[#F9FAFB] border-[1px] whitespace-nowrap border-[#F2F4F7] px-4 py-[14px] text-[#04080B]"
                      // >
                      //   <span className="text-[14px]">
                      //     {exportLoading ? (
                      //       <ButtonLoading thisColor={"#ddd"} />
                      //     ) : (
                      //       "Export CSV"
                      //     )}
                      //   </span>
                      //   <ChevronDown className="h-4 w-4" />
                      // </button>
                    )}
                  </>
                )}
                {draftBtn && !draftView && (
                  <button className={styles.draftBtn} onClick={onClickDraft}>
                    <span>{t("Draft")}</span>
                    <span className={styles.draftBtnCount}>{draftCount}</span>
                  </button>
                )}
                {draftView && (
                  <button className={styles.draftBtn} onClick={onClickDraft}>
                    <span className={styles.allEmpTxt}>
                      {t("All Employees")}
                    </span>
                    <span className={styles.draftBtnCount}>{allEmpCount}</span>
                  </button>
                )}
                {headerPrimaryBtnName && (
                  <button
                    className={styles.headerPrimaryBtn}
                    onClick={headerPrimaryBtnClick}
                  >
                    <img src={addIcon} alt="add icon" />
                    <span className={styles.headerBtnTxt}>
                      {t(headerPrimaryBtnName)}
                    </span>
                  </button>
                )}
                {headerSecondaryBtnName && (
                  <button
                    className={styles.headerSecondaryBtn}
                    onClick={headerSecondaryBtnClick}
                  >
                    <span className={styles.headerSecondaryBtnTxt}>
                      {t(headerSecondaryBtnName)}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {tabsOptions && (
          <div className={styles.addTabsContainer}>
            {tabsOptions.map((tab, index) => (
              <div
                key={tab.value}
                className={`add-tab cursor-pointer ${
                  activeTab === tab?.value ? "active" : ""
                }`}
                onClick={() => handleTabChange(tab.value)}
              >
                {t(tab.label)}
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <PageLoader />
        ) : (
          <>
            {statsData?.length > 0 && <Stats statsData={statsData} />}
            {additionalStats?.length > 0 && (
              <>
                <div className="employees-divs">
                  {additionalStats?.map((stat, index) => (
                    <div
                      className="each-employee-div"
                      key={stat.name}
                      onClick={() => handleAdditionalStatsClick(stat.param)}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className={`employee-div-formatting-state ${
                          stat.active ? "active-state" : ""
                        }`}
                      >
                        <div className="stats-icon">
                          <img src={stat.icon} alt={stat.iconAlt} />
                        </div>
                        <div className="stats-content">
                          <div className="stats-count">{stat.count}</div>
                          <div className="stats-label">{stat.name}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className={styles.tableContainer}>
              {isTable && (
                <div className={styles.tableWrapper}>
                  <table {...getTableProps()} className={styles.table}>
                    <thead>
                      {headerGroups.map((headerGroup) => {
                        const { key, ...restHeaderGroupProps } =
                          headerGroup.getHeaderGroupProps();
                        return (
                          <tr key={key} {...restHeaderGroupProps}>
                            {headerGroup.headers.map((column) => {
                              const { key, ...restColumnProps } =
                                column.getHeaderProps();

                              return (
                                <th
                                  key={key}
                                  {...restColumnProps}
                                  style={column.style}
                                  className={
                                    column.isFiltered ? styles.filteredTh : ""
                                  }
                                >
                                  {column.render("Header")}
                                </th>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {rows.map((row, index) => {
                        prepareRow(row);
                        const { key, ...restRowProps } = row.getRowProps();
                        return (
                          <tr
                            key={key}
                            {...restRowProps}
                            className={
                              index % 2 === 0
                                ? styles.tableRowEven
                                : styles.tableRowOdd
                            }
                          >
                            {row.cells.map((cell) => {
                              const { key, ...restCellProps } =
                                cell.getCellProps();

                              return (
                                <td
                                  key={key}
                                  {...restCellProps}
                                  style={cell.column.style}
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {isTable && noData && (
              <div className="w-full mt-4 flex items-center justify-center">
                <div className="bg-red-50 text-red-500 border border-red-200 rounded-xl px-6 py-5 shadow-sm max-w-md w-full text-center">
                  <h2 className="text-lg font-semibold">
                    {t("No data available")}
                  </h2>
                  {/* <p className="text-sm mt-1">
                    There’s currently nothing to display in this section.
                  </p> */}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TableComponent;
